import { getTelemetryContext, telemetryEnvironment } from "./telemetry-context";
import {
  confirmDiagnosticsConsent,
  diagnosticsAllowed,
  getPrivacyConsent,
  subscribePrivacyConsent,
  privacyDiagnosticsSyncFailed,
} from "./privacy-consent";
import { translate } from "@/i18n";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";

type SentryModule = typeof import("@sentry/react");
let sentry: SentryModule | null = null;
let subscribed = false;
let desired: boolean | undefined;
let generation = 0;
let configured: InitSentryOptions = {};
let pending: Promise<SentryModule | null> = Promise.resolve(null);

export function getSentry(): SentryModule | null {
  return diagnosticsAllowed() ? sentry : null;
}

export type InitSentryOptions = {
  dsn?: string;
  environment?: string;
};

async function applyConsent(): Promise<SentryModule | null> {
  const enabled = getPrivacyConsent().diagnostics;
  if (enabled === desired) return pending;
  desired = enabled;
  const current = ++generation;
  if (!enabled && sentry) {
    // The transport guard below blocks buffered envelopes before close flushes.
    const client = sentry.getClient();
    if (client) client.getOptions().enabled = false;
    void sentry.close(0);
    sentry = null;
  }
  if (current !== generation) return null;
  confirmDiagnosticsConsent(enabled);
  if (!enabled) return null;
  const dsn =
    configured.dsn ?? (import.meta.env.VITE_SENTRY_DSN as string | undefined);
  if (!dsn) return null;
  try {
    const Sentry = await import("@sentry/react");
    if (current !== generation || !diagnosticsAllowed()) return null;
    const telemetry = getTelemetryContext();
    Sentry.init({
      dsn,
      environment: configured.environment ?? telemetryEnvironment(),
      release:
        (import.meta.env.VITE_GIT_COMMIT_SHA as string) ||
        `denker@${telemetry.app_version}`,
      initialScope: { tags: { app_version: telemetry.app_version } },
      sendDefaultPii: false,
      sendClientReports: false,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      transport(options) {
        const transport = Sentry.makeFetchTransport(options);
        return {
          ...transport,
          send(envelope) {
            return current === generation && diagnosticsAllowed()
              ? transport.send(envelope)
              : Promise.resolve({});
          },
        };
      },
      beforeSend(event) {
        if (!diagnosticsAllowed()) return null;
        const message =
          event.message ?? event.exception?.values?.[0]?.value ?? "";
        if (
          typeof message === "string" &&
          /ResizeObserver loop|Non-Error promise rejection captured/.test(
            message,
          )
        )
          return null;
        if (event.request) {
          delete event.request.data;
          delete event.request.cookies;
          delete event.request.query_string;
          if (event.request.url)
            event.request.url = event.request.url.split(/[?#]/, 1)[0];
        }
        return event;
      },
      beforeSendTransaction: (event) => (diagnosticsAllowed() ? event : null),
    });
    sentry = Sentry;
    return Sentry;
  } catch {
    if (current === generation) {
      privacyDiagnosticsSyncFailed(
        translate(libNoticesMessages, "errorReportsFailed"),
      );
      desired = undefined;
    }
    return null;
  }
}

/** Register consent changes without loading or initializing optional diagnostics. */
export function initSentry(
  opts: InitSentryOptions = {},
): Promise<SentryModule | null> {
  configured = opts;
  if (!subscribed) {
    subscribed = true;
    subscribePrivacyConsent(() => {
      pending = applyConsent();
    });
  }
  pending = applyConsent();
  return pending;
}

export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  getSentry()?.captureException(
    error,
    context ? { extra: context } : undefined,
  );
}
